import type { DayPlan, WeekPlan } from '../../lib/apiClient';
import {
  PS_SELF_TALK_CATEGORIES,
  PS_SEVEN_DAY_CLOSING,
  PS_SEVEN_DAY_CLOSING_LEAD,
  PS_SEVEN_DAY_EXERCISES,
  PS_SEVEN_DAY_REFLECTION,
  PS_THIRTY_DAY_CLOSING,
  PS_THIRTY_DAY_REFLECTION,
  PS_THIRTY_DAY_STATEMENT_PROMPT,
  PS_THIRTY_DAY_WEEKS,
  PS_WEEKLY_REVIEW,
  PS_WHAT_THIS_PRACTICE_IS,
} from '../../lib/report/perspectiveWorkbookContent';

/**
 * The printable workbooks, copied from production's `bn-workbook-7day.tsx` and
 * `bn-workbook-30day.tsx`.
 *
 * **These are meant to be written in.** Production's whole point is a document
 * you print and fill out: every journal prompt is followed by ruled writing
 * lines, and each day ends with a **Daily Reflection** grid of four boxes.
 * V2 rendered the plan read-only, which is a summary of a workbook rather than
 * a workbook — its own footer even says "Print it, write in it, use it".
 *
 * The lines are decorative and `aria-hidden`: they are ruling for a printed
 * page, and a screen reader announcing a dozen empty boxes per day would bury
 * the prompts they belong to.
 */
const DAILY_REFLECTION_BOXES = [
  'What I completed today',
  'What I avoided today',
  'What I learned',
  'What I will do tomorrow',
];

/*
  Keys rather than an index, and a literal rather than `Array.from`: the
  no-browser-database rule scans for `.from(` and cannot tell `Array.from` from
  a Supabase query builder. Cheaper to sidestep than to weaken the rule.
*/
const LINE_KEYS = ['a', 'b', 'c', 'd', 'e'] as const;

/** Ruled lines to write on. Two is production's count under a prompt. */
function WriteLines({ count = 2 }: { count?: number }) {
  return (
    <div className="mt-1 flex flex-col" aria-hidden="true" data-testid="write-lines">
      {LINE_KEYS.slice(0, count).map((key) => (
        <span key={key} className="h-6 border-b border-border" />
      ))}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
      {children}
    </p>
  );
}

/** A 7-day plan as production's printable journal: one page per day. */
export function SevenDayWorkbook({ plan }: { plan: DayPlan[] }) {
  return (
    <div className="flex flex-col gap-4" data-testid="workbook-7day">
      {plan.map((day) => (
        <div
          key={day.day}
          className="overflow-hidden rounded-xl border border-border bg-card break-inside-avoid"
          data-testid="workbook-day"
          data-day={day.day}
        >
          <div className="flex items-baseline gap-3 border-b border-border bg-secondary/30 px-6 py-4">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              Day {day.day}
            </span>
            <h3 className="text-base font-bold">{day.focus}</h3>
          </div>

          <div className="flex flex-col gap-5 p-6">
            <div>
              <FieldLabel>Objective</FieldLabel>
              <p className="text-sm font-semibold">{day.objective}</p>
            </div>

            <div>
              <FieldLabel>Coaching Note</FieldLabel>
              <p className="text-sm leading-relaxed text-muted-foreground">{day.coachingNote}</p>
            </div>

            <div>
              <FieldLabel>Actions</FieldLabel>
              <ul className="flex flex-col gap-1.5">
                {day.actions.map((action) => (
                  <li key={action} className="text-sm leading-relaxed">
                    {action}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <FieldLabel>Journal Prompts</FieldLabel>
              <div className="flex flex-col gap-3">
                {day.journalPrompts.map((prompt) => (
                  <div key={prompt}>
                    <p className="text-sm italic leading-relaxed text-muted-foreground">{prompt}</p>
                    <WriteLines />
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <FieldLabel>Daily Reflection</FieldLabel>
              <div className="grid gap-4 sm:grid-cols-2" data-testid="daily-reflection">
                {DAILY_REFLECTION_BOXES.map((label) => (
                  <div key={label} data-testid="reflection-box">
                    <p className="text-xs font-medium text-muted-foreground">{label}</p>
                    <WriteLines />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Production's closing panel on the 30-day workbook. Three numbered notes on
 * what to do once the thirty days are done.
 */
/** Production's three questions closing every week of the 30-day workbook. */
/** Production's tick boxes. Literals rather than a range, for the same
    reason LINE_KEYS is: the no-browser-database rule scans for `.from(`. */
const DAY_NUMBERS = [1, 2, 3, 4, 5, 6, 7];
const WEEK_NUMBERS = [1, 2, 3, 4];

const WEEK_REVIEW = [
  'What worked this week?',
  'What did I struggle with?',
  'What will I do differently next week?',
];

/** And the two it closes the thirty days with. */
const THIRTY_DAY_REVIEW = [
  'What changed over these 30 days?',
  'What still needs work?',
  'What is my next bottleneck to address?',
];

const THIRTY_DAY_CLOSING = [
  {
    n: '01',
    title: 'Compare your scores',
    body: 'Review your original assessment scores against your reassessment scores. See what shifted and where you still have drag.',
  },
  {
    n: '02',
    title: 'Identify your next bottleneck',
    body: 'Every bottleneck you clear reveals the next one. Your performance ceiling rises as you address each layer.',
  },
  {
    n: '03',
    title: 'This is a long game',
    body: 'High performance is not a one-time fix. It is a continuous practice of identifying, addressing, and moving past each limiting pattern.',
  },
];

/**
 * A 30-day plan as production's printable workbook: one page per week.
 *
 * Production gives the weekly workbook **no** reflection grid — the daily
 * journal has one and the weekly one does not. Reproduced.
 */
export function ThirtyDayWorkbook({
  plan,
  closing = false,
}: {
  plan: WeekPlan[];
  /**
   * Whether to print the Bottleneck workbook's closing panel.
   *
   * Off by default, because this component renders in two places: the
   * standalone Bottleneck 30-day workbook, where production closes with "This
   * is a long game", and beneath the Perspective workbook's fixed programme,
   * where production closes with three different steps of its own. Rendering
   * the panel unconditionally put the Bottleneck closing on the Perspective
   * page.
   */
  closing?: boolean;
}) {
  return (
    <div className="flex flex-col gap-4" data-testid="workbook-30day">
      {plan.map((week) => (
        <div
          key={week.week}
          className="overflow-hidden rounded-xl border border-border bg-card break-inside-avoid"
          data-testid="workbook-week"
          data-week={week.week}
        >
          <div className="border-b border-border bg-secondary/30 px-6 py-4">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              Week {week.week}
            </span>
            <h3 className="text-base font-bold">{week.theme}</h3>
          </div>

          <div className="flex flex-col gap-5 p-6">
            <div>
              <FieldLabel>Goals</FieldLabel>
              <ul className="flex flex-col gap-1.5">
                {week.goals.map((goal) => (
                  <li key={goal} className="text-sm leading-relaxed">
                    {goal}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <FieldLabel>Key Behaviors</FieldLabel>
              <ul className="flex flex-col gap-1.5">
                {week.behaviors.map((behavior) => (
                  <li key={behavior} className="text-sm leading-relaxed">
                    {behavior}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <FieldLabel>Journal Prompts</FieldLabel>
              <div className="flex flex-col gap-3">
                {week.journalPrompts.map((prompt) => (
                  <div key={prompt}>
                    <p className="text-sm italic leading-relaxed text-muted-foreground">{prompt}</p>
                    <WriteLines count={3} />
                  </div>
                ))}
              </div>
            </div>

            {/* Production closes every week with the same three questions. */}
            <div className="border-t border-border pt-4" data-testid="workbook-week-review">
              <FieldLabel>Week {week.week} Review</FieldLabel>
              <div className="flex flex-col gap-3">
                {WEEK_REVIEW.map((prompt) => (
                  <div key={prompt}>
                    <p className="text-sm italic leading-relaxed text-muted-foreground">{prompt}</p>
                    <WriteLines />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Production opens the 30-day workbook with two boxes to tick off. */}
      {closing ? (
      <div
        className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 break-inside-avoid"
        data-testid="workbook-trackers"
      >
        <div>
          <FieldLabel>7-Day Reset Completion</FieldLabel>
          <div className="flex gap-1">
            {DAY_NUMBERS.map((day) => (
              <span key={day} className="flex flex-col items-center gap-1">
                <span className="text-[10px] text-muted-foreground">D{day}</span>
                <span className="h-6 w-6 rounded border border-border" aria-hidden="true" />
              </span>
            ))}
          </div>
        </div>
        <div>
          <FieldLabel>30-Day Weekly Tracker</FieldLabel>
          <div className="flex gap-2">
            {WEEK_NUMBERS.map((week) => (
              <span key={week} className="flex flex-1 flex-col items-center gap-1">
                <span className="text-[10px] text-muted-foreground">Week {week}</span>
                <span className="aspect-square w-full rounded border border-border" aria-hidden="true" />
              </span>
            ))}
          </div>
        </div>
      </div>
      ) : null}

      {/* Production closes the thirty days with these, on the Bottleneck
          workbook only. */}
      {closing ? (
      <div
        className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 break-inside-avoid"
        data-testid="workbook-thirty-day-review"
      >
        <FieldLabel>30-Day Review</FieldLabel>
        {THIRTY_DAY_REVIEW.map((prompt) => (
          <div key={prompt}>
            <p className="text-sm italic leading-relaxed text-muted-foreground">{prompt}</p>
            <WriteLines count={3} />
          </div>
        ))}
      </div>
      ) : null}

      {closing ? (
      <div
        className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 break-inside-avoid"
        data-testid="workbook-closing"
      >
        <h3 className="text-base font-bold">Thirty days complete.</h3>
        <p className="max-w-lg text-sm leading-relaxed text-muted-foreground">
          You have completed the full 30-Day Reset Workbook. Return to High Performer OS to retake
          the assessment and see what shifted.
        </p>
        {THIRTY_DAY_CLOSING.map((step) => (
          <div key={step.n} className="flex gap-4" data-testid="workbook-closing-step">
            <span className="min-w-7 pt-px text-xs font-extrabold text-primary">{step.n}</span>
            <p className="text-sm leading-relaxed text-muted-foreground">
              <strong className="font-bold text-foreground">{step.title}.</strong> {step.body}
            </p>
          </div>
        ))}
      </div>
      ) : null}
    </div>
  );
}

/**
 * The Perspective workbooks, copied from production's
 * `perspective-workbook-7day.tsx` and `perspective-workbook-30day.tsx`.
 *
 * **These are not the Bottleneck workbooks with different data.** Production
 * runs a *fixed* programme here — the same seven days and four weeks for every
 * customer, each with its own title, subtitle and prompts — and shows the
 * personalised plan alongside it. V2 rendered the Bottleneck shape for both
 * types, so none of this existed.
 */
export function PerspectiveSevenDayWorkbook({
  activeTraps,
  dominantPattern,
  immediateReframe,
}: {
  /** Production lists the customer's active traps before the seven days. */
  activeTraps: Array<{ questionId: string; label: string; description: string }>;
  dominantPattern?: string;
  immediateReframe?: string;
}) {
  return (
    <div className="flex flex-col gap-4" data-testid="perspective-workbook-7day">
      {/* Production opens the workbook by restating what it is for. */}
      {dominantPattern || immediateReframe ? (
        <div
          className="flex flex-col gap-3 rounded-xl border border-border bg-card p-6"
          data-testid="perspective-workbook-cover"
        >
          {dominantPattern ? (
            <div>
              <FieldLabel>Your Dominant Pattern</FieldLabel>
              <p className="text-sm font-semibold">{dominantPattern}</p>
            </div>
          ) : null}
          {immediateReframe ? (
            <div>
              <FieldLabel>Your Immediate Reframe</FieldLabel>
              <p className="text-sm italic leading-relaxed text-muted-foreground">
                {immediateReframe}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        className="flex flex-col gap-2 rounded-xl border border-border bg-card p-6"
        data-testid="perspective-workbook-index"
      >
        <FieldLabel>The Seven Days</FieldLabel>
        <ol className="flex flex-col gap-1">
          {PS_SEVEN_DAY_EXERCISES.map((day) => (
            <li key={day.n} className="text-sm text-muted-foreground">
              Day {day.n}: {day.title}
            </li>
          ))}
        </ol>
      </div>
      {activeTraps.length > 0 ? (
        <div
          className="flex flex-col gap-2 rounded-xl border border-border bg-card p-6"
          data-testid="perspective-active-traps"
        >
          <FieldLabel>Active Interpretation Traps to Watch</FieldLabel>
          <ul className="flex flex-col gap-2">
            {activeTraps.map((trap) => (
              <li key={trap.questionId} className="text-sm leading-relaxed">
                <strong>{trap.label}.</strong> {trap.description}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {PS_SEVEN_DAY_EXERCISES.map((day) => (
        <div
          key={day.n}
          className="overflow-hidden rounded-xl border border-border bg-card break-inside-avoid"
          data-testid="perspective-workbook-day"
          data-day={day.n}
        >
          <div className="flex items-baseline gap-3 border-b border-border bg-secondary/30 px-6 py-4">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              Day {day.n}
            </span>
            <h3 className="text-base font-bold">{day.title}</h3>
          </div>
          <div className="flex flex-col gap-4 p-6">
            <p className="text-sm leading-relaxed text-muted-foreground">{day.subtitle}</p>
            {day.prompts.map((prompt) => (
              <div key={prompt}>
                <p className="text-sm italic leading-relaxed text-muted-foreground">{prompt}</p>
                <WriteLines />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div
        className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 break-inside-avoid"
        data-testid="perspective-workbook-reflection"
      >
        <FieldLabel>7-Day Reflection</FieldLabel>
        {PS_SEVEN_DAY_REFLECTION.map((prompt) => (
          <div key={prompt}>
            <p className="text-sm italic leading-relaxed text-muted-foreground">{prompt}</p>
            <WriteLines />
          </div>
        ))}
      </div>

      <div
        className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 break-inside-avoid"
        data-testid="perspective-seven-day-closing"
      >
        <h3 className="text-base font-bold">What Comes Next</h3>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {PS_SEVEN_DAY_CLOSING_LEAD}
        </p>
        {PS_SEVEN_DAY_CLOSING.map((step, index) => (
          <div key={step.title} className="flex gap-4" data-testid="perspective-seven-day-step">
            <span className="min-w-7 pt-px text-xs font-extrabold text-primary">
              {String(index + 1).padStart(2, '0')}
            </span>
            <p className="text-sm leading-relaxed text-muted-foreground">
              <strong className="font-bold text-foreground">{step.title}</strong> {step.body}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PerspectiveThirtyDayWorkbook({
  dominantPattern,
  immediateReframe,
}: {
  dominantPattern?: string;
  immediateReframe?: string;
} = {}) {
  return (
    <div className="flex flex-col gap-4" data-testid="perspective-workbook-30day">
      <div
        className="flex flex-col gap-3 rounded-xl border border-border bg-card p-6"
        data-testid="perspective-workbook-profile"
      >
        <h3 className="text-base font-bold">Your Profile at a Glance</h3>
        {dominantPattern ? (
          <div>
            <FieldLabel>Your Dominant Pattern</FieldLabel>
            <p className="text-sm font-semibold">{dominantPattern}</p>
          </div>
        ) : null}
        {immediateReframe ? (
          <div>
            <FieldLabel>Your Immediate Reframe</FieldLabel>
            <p className="text-sm italic leading-relaxed text-muted-foreground">
              {immediateReframe}
            </p>
          </div>
        ) : null}
        <div>
          <FieldLabel>What This Practice Is</FieldLabel>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {PS_WHAT_THIS_PRACTICE_IS}
          </p>
        </div>
      </div>
      {PS_THIRTY_DAY_WEEKS.map((week) => (
        <div
          key={week.n}
          className="overflow-hidden rounded-xl border border-border bg-card break-inside-avoid"
          data-testid="perspective-workbook-week"
          data-week={week.n}
        >
          <div className="border-b border-border bg-secondary/30 px-6 py-4">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              Week {week.n}
            </span>
            <h3 className="text-base font-bold">{week.theme}</h3>
          </div>
          <div className="flex flex-col gap-4 p-6">
            <div>
              <FieldLabel>Goal</FieldLabel>
              <p className="text-sm font-semibold">{week.goal}</p>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">{week.intro}</p>
            {/* Production lists these on week 2 and only there. */}
            {week.n === 2 ? (
              <div data-testid="perspective-self-talk-categories">
                <FieldLabel>Self-Talk Categories</FieldLabel>
                <ul className="flex flex-wrap gap-2">
                  {PS_SELF_TALK_CATEGORIES.map((category) => (
                    <li
                      key={category}
                      className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground"
                    >
                      {category}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div>
              <FieldLabel>Daily Prompts</FieldLabel>
              <div className="flex flex-col gap-3">
                {week.dailyPrompts.map((prompt) => (
                  <div key={prompt}>
                    <p className="text-sm italic leading-relaxed text-muted-foreground">{prompt}</p>
                    <WriteLines />
                  </div>
                ))}
              </div>
            </div>

            {/* Production closes every week with the same two questions. */}
            <div className="border-t border-border pt-4" data-testid="perspective-weekly-review">
              <FieldLabel>Weekly Review</FieldLabel>
              <div className="flex flex-col gap-3">
                {PS_WEEKLY_REVIEW.map((prompt) => (
                  <div key={prompt}>
                    <p className="text-sm italic leading-relaxed text-muted-foreground">{prompt}</p>
                    <WriteLines />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}

      <div
        className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 break-inside-avoid"
        data-testid="perspective-thirty-day-reflection"
      >
        <FieldLabel>30-Day Reflection</FieldLabel>
        {PS_THIRTY_DAY_REFLECTION.map((prompt) => (
          <div key={prompt}>
            <p className="text-sm italic leading-relaxed text-muted-foreground">{prompt}</p>
            <WriteLines />
          </div>
        ))}
        <div>
          <p className="text-sm font-medium">{PS_THIRTY_DAY_STATEMENT_PROMPT}</p>
          {/* Production gives the statement six lines, not two. */}
          <WriteLines count={5} />
        </div>
      </div>

      <div
        className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 break-inside-avoid"
        data-testid="perspective-thirty-day-closing"
      >
        {PS_THIRTY_DAY_CLOSING.map((step, index) => (
          <div key={step.title} className="flex gap-4" data-testid="perspective-closing-step">
            <span className="min-w-7 pt-px text-xs font-extrabold text-primary">
              {String(index + 1).padStart(2, '0')}
            </span>
            <p className="text-sm leading-relaxed text-muted-foreground">
              <strong className="font-bold text-foreground">{step.title}</strong> {step.body}
            </p>
          </div>
        ))}
        <p className="text-xs leading-relaxed text-muted-foreground">
          High Performer OS is for personal development and coaching purposes only. It is not
          medical, psychological, or clinical advice and is not a diagnostic instrument.
        </p>
      </div>
    </div>
  );
}
